import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { ViewOnly, WriteOnly } from "@/components/shell";

import {
  listEveryFact,
  listFacts,
  listRequests,
  type DecideIn,
  type DecisionRequest,
} from "./api";
import { DecidedRecord } from "./components/DecidedRecord";
import { DecisionForm } from "./components/DecisionForm";
import { RequestRow } from "./components/RequestRow";
import {
  ALREADY_DECIDED,
  apiStatusFor,
  COVERAGE_FACT_KEY,
  deferredAgo,
  emptyForm,
  formulasFrom,
  isDeferred,
  listFailureMessage,
  marketsFrom,
  nameFromEmail,
  pageCount,
  pageFrom,
  PAGE_SIZE,
  paneRequest,
  rationaleOf,
  requestsKey,
  shortDate,
  statusFilterFrom,
  submitDecision,
  topicsFrom,
  waitingLabel,
  WAITING_FOR_OWNER,
  type DecideFailure,
  type FormState,
  type FormulaOption,
  type MarketOption,
  type StatusFilter,
  type TopicOption,
} from "./inbox";
import { useInboxLayout } from "./useInboxLayout";

const FILTERS: Array<{ key: StatusFilter; label: string }> = [
  { key: "waiting", label: "Waiting" },
  { key: "decided", label: "Decided" },
  { key: "all", label: "All" },
];

const EMPTY_COPY: Record<StatusFilter, { title: string; hint?: string }> = {
  waiting: {
    title: "Nothing waiting for a decision.",
    hint: "New questions appear here when /cvce-cx files one.",
  },
  decided: { title: "No decisions yet." },
  all: { title: "No requests yet." },
};

/**
 * The Inbox tab: policy questions the CVCE front door filed, and the one screen where
 * the Protex owner decides them (UX v2 S4 + S5).
 *
 * No `PermissionGate`, no `decision` prop, no tab bar (I-7). Every Protex viewer sees the
 * tab (`min_level: view`, GATE-476) and reads requests and outcomes. The decision form
 * sits in the shell's `WriteOnly`, so it is not rendered for a viewer, who reads the
 * `ViewOnly` waiting line instead; the shell decides both. The API's 403 stays this
 * module's only permission signal. A 403 or 503 renders an alert and NO rows, never
 * an empty list, because an empty list asserts "nothing is waiting", which we do not know.
 */
export function InboxTab(_props: { areaName: string }) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const status = statusFilterFrom(search.status);
  const askedPage = pageFrom(search.page);
  const page = askedPage;

  // Once anything has been decided, reads bypass the browser's 30 s cache: the list
  // is `private, max-age=30`, so a plain refetch could show the row still waiting.
  const fresh = useRef(false);
  const { ref: layoutRef, split } = useInboxLayout<HTMLDivElement>();
  // State rather than a ref: the sheet and the dialog mount into this anchor, so it has
  // to be a rendered element by the time they do (they would otherwise fall back to the
  // body portal and leave the shell's permission gate).
  const [sheetAnchor, setSheetAnchor] = useState<HTMLDivElement | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selectedRef = useRef<string | null>(null);
  selectedRef.current = selectedId;
  const [sheetOpen, setSheetOpen] = useState(false);
  // Drafts live HERE, keyed by request, not inside the form: a reason typed into a form
  // that a 409 then replaces with the decided record must survive the swap.
  const [drafts, setDrafts] = useState<Record<string, FormState>>({});
  const [failures, setFailures] = useState<Record<string, DecideFailure>>({});
  const [conflicts, setConflicts] = useState<Record<string, { reason: string }>>({});
  // What was just decided, said out loud: the pane moves on to the next request, so
  // without this line the move is silent and the owner cannot tell it landed.
  const [recorded, setRecorded] = useState<string | null>(null);

  const list = useQuery({
    queryKey: requestsKey(status, page),
    queryFn: () =>
      listRequests({
        status: apiStatusFor(status),
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
        fresh: fresh.current,
      }),
  });
  // The "Waiting N" count must stay true while another filter is showing.
  const waitingCount = useQuery({
    queryKey: ["protex", "requests", "waiting-count"],
    queryFn: () => listRequests({ status: "open", limit: 1, offset: 0, fresh: fresh.current }),
    enabled: status !== "waiting",
  });
  // The shared read the Facts tab and the module head already make: same key, same reader.
  const facts = useQuery({
    queryKey: ["protex", "facts", "all"],
    queryFn: () => listEveryFact({}, { limit: 200 }),
  });
  const coverage = useQuery({
    queryKey: ["protex", "facts", "coverage"],
    queryFn: () =>
      listFacts({ fact_key: COVERAGE_FACT_KEY, status: "approved", limit: 200, offset: 0 }),
  });

  const decide = useMutation({
    mutationFn: ({ id, body }: { id: string; body: DecideIn }) => submitDecision(id, body),
    onSuccess: (result, { id }) => {
      fresh.current = true;
      if (result.ok) {
        const without = <T,>(map: Record<string, T>) =>
          Object.fromEntries(Object.entries(map).filter(([key]) => key !== id));
        setDrafts(without);
        setFailures(without);
        setConflicts(without);
        setRecorded(
          result.request.status === "decided"
            ? "Decision recorded. Vox can cite it now."
            : "Deferred. The request stays in Waiting.",
        );
        // Close the phone sheet and clear the selection, so the pane never slides
        // to a different question under the owner's thumb.
        setSheetOpen(false);
        setSelectedId(null);
      } else if (result.failure.kind === "conflict") {
        // Someone decided first. Keep the words that were typed, then show the record.
        setConflicts((prev) => ({ ...prev, [id]: { reason: drafts[id]?.rationale ?? "" } }));
        if (selectedRef.current === id || selectedRef.current === null) {
          setSelectedId(id);
          setUrl({ status: "decided", page: 1 });
        }
      } else {
        setFailures((prev) => ({ ...prev, [id]: result.failure }));
      }
      void queryClient.invalidateQueries({ queryKey: ["protex", "requests"] });
      void queryClient.invalidateQueries({ queryKey: ["protex", "facts"] });
    },
  });

  function setUrl(patch: { status?: StatusFilter; page?: number }) {
    const next = { status, page, ...patch };
    const options: Record<string, unknown> = {
      search: (prev: Record<string, unknown>) => {
        const merged: Record<string, unknown> = { ...prev, status: next.status, page: next.page };
        if (next.status === "waiting") delete merged.status;
        if (next.page === 1) delete merged.page;
        return merged;
      },
      replace: true,
    };
    void navigate(options as never);
  }

  const items = list.data?.data.items ?? [];
  const total = list.data?.data.total ?? 0;
  // A page past the end (the last row on it was just decided, or a stale link) moves
  // back to the last real page rather than claiming nothing is waiting.
  useEffect(() => {
    if (list.data && !list.isError && total > 0 && page > pageCount(total)) {
      setUrl({ page: pageCount(total) });
    }
  }, [list.data, list.isError, total, page]);
  const pane = paneRequest(items, selectedId);
  // No count until a read has answered: "Waiting 0" over a refused or loading list
  // asserts "nothing is waiting", the same false claim an empty list would make.
  const waiting =
    status === "waiting"
      ? list.data && !list.isError
        ? total
        : null
      : waitingCount.isError
        ? null
        : (waitingCount.data?.data.total ?? null);
  const coverageFacts = coverage.data?.data.items ?? [];
  const factItems = facts.data?.data.items ?? [];
  const formulas = formulasFrom(factItems);
  const markets = marketsFrom(factItems);
  const now = new Date();

  const draftFor = (request: DecisionRequest) => drafts[request.request_id] ?? emptyForm(request);
  const onDraft = (request: DecisionRequest, next: FormState) => {
    setDrafts((prev) => ({ ...prev, [request.request_id]: next }));
    // Editing again clears what the server said about the last attempt.
    setFailures((prev) => Object.fromEntries(Object.entries(prev).filter(([k]) => k !== request.request_id)));
  };
  const select = (request: DecisionRequest) => {
    setRecorded(null);
    setSelectedId(request.request_id);
    setSheetOpen(true);
  };

  const retry = () => {
    fresh.current = true;
    void list.refetch();
    void facts.refetch();
    void coverage.refetch();
  };

  const conflictOnly =
    selectedId !== null &&
    conflicts[selectedId] !== undefined &&
    !items.some((r) => r.request_id === selectedId);

  const paneNode = conflictOnly ? (
    <div className="space-y-4">
      <Alert variant="destructive">
        <AlertDescription>{ALREADY_DECIDED}</AlertDescription>
      </Alert>
      <div className="space-y-1 rounded-lg border border-ks-grey-4 p-3">
        <p className="text-xs font-medium text-ks-grey-1">
          Your reason was not saved. It is here so you can copy it.
        </p>
        <p className="text-sm whitespace-pre-wrap text-ks-ink select-all">
          {conflicts[selectedId as string].reason}
        </p>
      </div>
    </div>
  ) : pane ? (
    <RequestPane
      request={pane}
      now={now}
      draft={draftFor(pane)}
      onDraft={(next) => onDraft(pane, next)}
      conflictReason={conflicts[pane.request_id]?.reason}
      failure={failures[pane.request_id] ?? null}
      topics={topicsFrom(coverageFacts, draftFor(pane).region)}
      topicsFailed={coverage.isError}
      formulas={formulas}
      formulasFailed={facts.isError}
      markets={markets}
      pending={decide.isPending && decide.variables?.id === pane.request_id}
      container={sheetAnchor}
      onSubmit={(body) => decide.mutate({ id: pane.request_id, body })}
    />
  ) : (
    <p className="text-sm text-ks-grey-1">Select a request to see it.</p>
  );

  const pages = pageCount(total);

  return (
    <div
      ref={layoutRef}
      data-layout={split ? "split" : "stacked"}
      className={
        split
          ? "grid grid-cols-[minmax(0,26rem)_minmax(0,1fr)] gap-6"
          : "grid grid-cols-1 gap-6"
      }
    >
      <section aria-label="Requests" className="min-w-0 space-y-4">
        {recorded && (
          <p role="status" className="text-sm font-medium text-ks-ink">
            {recorded}
          </p>
        )}
        <div role="group" aria-label="Show requests" className="flex flex-wrap gap-2">
          {FILTERS.map(({ key, label }) => (
            <Button
              key={key}
              type="button"
              size="sm"
              variant={status === key ? "default" : "outline"}
              aria-pressed={status === key}
              data-nonmutating
              onClick={() => setUrl({ status: key, page: 1 })}
            >
              {label}
              {key === "waiting" && waiting !== null ? ` ${waiting}` : ""}
            </Button>
          ))}
        </div>

        {list.isError ? (
          // Zero rows. An unreachable or refusing list is not an empty one.
          <div className="space-y-3">
            <Alert variant="destructive">
              <AlertDescription>{listFailureMessage(list.error)}</AlertDescription>
            </Alert>
            <Button type="button" variant="outline" size="sm" data-nonmutating onClick={retry}>
              Try again
            </Button>
          </div>
        ) : list.isPending ? (
          <div className="space-y-2" aria-busy="true">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-16 w-full rounded-lg" />
            ))}
          </div>
        ) : items.length === 0 && total === 0 ? (
          <div className="space-y-1 text-sm">
            <p className="font-medium text-ks-ink">{EMPTY_COPY[status].title}</p>
            {EMPTY_COPY[status].hint && <p className="text-ks-grey-1">{EMPTY_COPY[status].hint}</p>}
          </div>
        ) : (
          <>
            <ul className="space-y-2">
              {items.map((request) => (
                <RequestRow
                  key={request.request_id}
                  request={request}
                  now={now}
                  selected={pane?.request_id === request.request_id}
                  onSelect={() => select(request)}
                />
              ))}
            </ul>
            <nav aria-label="Pages" className="flex items-center justify-end gap-2 text-sm">
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                data-nonmutating
                aria-label="Previous page"
                disabled={page <= 1}
                onClick={() => setUrl({ page: page - 1 })}
              >
                <ChevronLeft />
              </Button>
              <span>
                {page} of {pages}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                data-nonmutating
                aria-label="Next page"
                disabled={page >= pages}
                onClick={() => setUrl({ page: page + 1 })}
              >
                <ChevronRight />
              </Button>
            </nav>
          </>
        )}
      </section>

      {split && !list.isError && (
        <section aria-label="Request" className="min-w-0 rounded-xl border border-ks-grey-4 p-5">
          {paneNode}
        </section>
      )}

      <div ref={setSheetAnchor} className="contents" />

      {!split && sheetAnchor !== null && !list.isError && (
        <Sheet open={sheetOpen && (pane !== null || conflictOnly)} onOpenChange={setSheetOpen}>
          <SheetContent
            side="bottom"
            container={sheetAnchor}
            className="h-[92vh] overflow-y-auto rounded-t-3xl"
          >
            <SheetHeader className="pb-0">
              <SheetTitle className="sr-only">Request</SheetTitle>
            </SheetHeader>
            <div className="px-4 pb-8">{paneNode}</div>
          </SheetContent>
        </Sheet>
      )}
    </div>
  );
}

/**
 * One request and what can be done with it: its question and age, then the form while
 * it is open, or the record once it is decided. Exported so the 409 swap can be rendered
 * and read directly.
 */
export function RequestPane({
  request,
  now,
  draft,
  onDraft,
  conflictReason,
  failure,
  topics,
  topicsFailed,
  formulas,
  formulasFailed = false,
  markets,
  pending,
  container,
  onSubmit,
}: {
  request: DecisionRequest;
  now?: Date;
  draft: FormState;
  onDraft: (next: FormState) => void;
  conflictReason?: string;
  failure: DecideFailure | null;
  topics: readonly TopicOption[];
  topicsFailed: boolean;
  formulas: readonly FormulaOption[];
  formulasFailed?: boolean;
  markets: readonly MarketOption[];
  pending: boolean;
  container: HTMLElement | null;
  onSubmit: (body: DecideIn) => void;
}) {
  const deferred = isDeferred(request);
  return (
    <div className="space-y-5">
      <header className="space-y-1.5">
        <h2 className="text-lg font-bold text-ks-ink">{request.question_text}</h2>
        <p className="text-sm text-ks-grey-1">
          Raised by {nameFromEmail(request.raised_by)}
          {request.status === "open"
            ? ` · ${waitingLabel(request.created_at, now)}`
            : ` · ${shortDate(request.created_at)}`}
          {request.conversation_id && (
            <>
              {" · conversation "}
              <span className="select-all">{request.conversation_id}</span>
            </>
          )}
        </p>
      </header>

      {conflictReason !== undefined && (
        <Alert variant="destructive">
          <AlertDescription>{ALREADY_DECIDED}</AlertDescription>
        </Alert>
      )}

      {request.status === "decided" ? (
        <DecidedRecord request={request} unsavedReason={conflictReason || undefined} />
      ) : request.status === "withdrawn" ? (
        <p className="text-sm text-ks-grey-1">This request was withdrawn. There is nothing to decide.</p>
      ) : (
        <>
          {deferred && (
            <p className="rounded-lg bg-ks-grey-4 px-3 py-2 text-sm text-ks-ink">
              Deferred{request.decision?.decided_at ? ` on ${shortDate(String(request.decision.decided_at))}` : ""}
              {" by "}
              {nameFromEmail(String(request.decision?.decided_by ?? ""))}: {rationaleOf(request)}
              {deferredAgo(request, now) ? ` (${deferredAgo(request, now)})` : ""}
            </p>
          )}
          <ViewOnly>
            <p className="text-sm text-ks-grey-1">{WAITING_FOR_OWNER}</p>
          </ViewOnly>
          <WriteOnly>
            <DecisionForm
              key={request.request_id}
              request={request}
              state={draft}
              onChange={onDraft}
              topics={topics}
              topicsFailed={topicsFailed}
              formulas={formulas}
              formulasFailed={formulasFailed}
              markets={markets}
              serverProblems={failure?.kind === "fields" ? failure.problems : {}}
              failure={failure}
              pending={pending}
              container={container}
              onSubmit={onSubmit}
            />
          </WriteOnly>
        </>
      )}
    </div>
  );
}
